// Shared constants, design tokens, style objects, the MUI theme, and pure
// validation/formatting helpers used by the Payment screen and its extracted
// sub-components. Kept in one module so there is a single source of truth and
// no circular import back into the main component. This is a verbatim
// extraction of the module-level code that previously lived at the top of
// PaymentScreen.js — behaviour is unchanged.
import { createTheme } from "@mui/material";

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// ─── Blue/White Design Tokens ───────────────────────────────────────────────
export const BLUE = "#2563EB";
export const BLUE_DARK = "#1D4ED8";
export const BLUE_MED = "#3B82F6";
export const BLUE_LIGHT = "#DBEAFE";
export const BLUE_PALE = "#EFF6FF";
export const BLUE_SUBTLE = "#F0F7FF";

export const INK = "#0F172A";
export const INK_SOFT = "#334155";
export const INK_MUTED = "#64748B";
export const BORDER = "#E2E8F0";
export const BORDER_MED = "#CBD5E1";
export const SURFACE = "#FFFFFF";
export const BG = "#F8FAFC";
export const BG_ALT = "#F1F5F9";

export const SUCCESS = "#059669";
export const SUCCESS_BG = "#ECFDF5";
export const ERROR_C = "#DC2626";
export const ERROR_BG = "#FEF2F2";
export const WARN = "#D97706";
export const INFO_C = "#0284C7";

// ─── Validation helpers ───────────────────────────────────────────────────────────
export const preventNegativeInput = (e, setterFunction, fieldName) => {
  let value = e.target.value;
  if (value === "" || value === null || value === undefined) {
    setterFunction((prev) => ({ ...prev, [fieldName]: "" }));
    return;
  }
  const num = parseFloat(value);
  if (!isNaN(num) && num < 0) {
    setterFunction((prev) => ({ ...prev, [fieldName]: 0 }));
  } else {
    setterFunction((prev) => ({ ...prev, [fieldName]: value }));
  }
};

export const preventNegativeNumber = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return "";
  return num < 0 ? 0 : num;
};

export const validateMobile = (value) => {
  const cleaned = value?.toString().replace(/\D/g, "") || "";
  if (cleaned.length === 0) return { isValid: true, value: "", error: "" };
  if (cleaned.length > 10)
    return {
      isValid: false,
      value: cleaned.slice(0, 10),
      error: "Mobile number must be exactly 10 digits",
    };
  if (cleaned.length === 10)
    return { isValid: true, value: cleaned, error: "" };
  return { isValid: true, value: cleaned, error: "" }; // Allow partial input
};

// Re-exported so every pawn screen entering a mobile number behaves the same:
// a number pad on phones/tablets, digits only from a hardware keyboard, 10 max.
// Defined once in pawnCustomer/shared.js — do not fork a second copy here.
export { mobileFieldProps, sanitizeMobileInput } from "../pawnCustomer/shared";

export const validateAadhar = (value) => {
  const cleaned = value?.toString().replace(/\D/g, "") || "";
  if (cleaned.length === 0) return { isValid: true, value: "", error: "" };
  if (cleaned.length > 12)
    return {
      isValid: false,
      value: cleaned.slice(0, 12),
      error: "Aadhar number must be exactly 12 digits",
    };
  if (cleaned.length === 12)
    return { isValid: true, value: cleaned, error: "" };
  return { isValid: true, value: cleaned, error: "" };
};

export const validatePAN = (value) => {
  const cleaned = value?.toString().toUpperCase() || "";
  // Allow only alphanumeric and limit to 10 characters
  const alphanumeric = cleaned.replace(/[^A-Z0-9]/g, "");
  if (alphanumeric.length === 0) return { isValid: true, value: "", error: "" };
  if (alphanumeric.length > 10)
    return {
      isValid: false,
      value: alphanumeric.slice(0, 10),
      error: "PAN card must be exactly 10 alphanumeric characters",
    };
  if (alphanumeric.length === 10) {
    // Optional: Basic PAN format validation (5 letters, 4 numbers, 1 letter)
    const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    if (!panPattern.test(alphanumeric)) {
      return {
        isValid: true,
        value: alphanumeric,
        error: "Invalid PAN format (e.g., ABCDE1234F)",
      };
    }
    return { isValid: true, value: alphanumeric, error: "" };
  }
  return { isValid: true, value: alphanumeric, error: "" };
};

// ─── Amount-in-words (Indian numbering) ──────────────────────────────────────
// Used to echo the loan amount in words right beside the Loan Amount field so
// the teller can sanity-check the figure while typing.
export const numberToWords = (num) => {
  const n = Math.floor(Math.abs(parseFloat(num) || 0));
  if (!n) return "";
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const teens = [
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const convert = (x) => {
    if (x === 0) return "";
    if (x < 10) return ones[x];
    if (x < 20) return teens[x - 10];
    if (x < 100)
      return tens[Math.floor(x / 10)] + (x % 10 ? " " + ones[x % 10] : "");
    if (x < 1000)
      return (
        ones[Math.floor(x / 100)] +
        " Hundred" +
        (x % 100 ? " " + convert(x % 100) : "")
      );
    if (x < 100000)
      return (
        convert(Math.floor(x / 1000)) +
        " Thousand" +
        (x % 1000 ? " " + convert(x % 1000) : "")
      );
    if (x < 10000000)
      return (
        convert(Math.floor(x / 100000)) +
        " Lakh" +
        (x % 100000 ? " " + convert(x % 100000) : "")
      );
    return (
      convert(Math.floor(x / 10000000)) +
      " Crore" +
      (x % 10000000 ? " " + convert(x % 10000000) : "")
    );
  };
  return convert(n) + " Rupees Only";
};

// Government-approved official monthly interest cap (%). A loaded voucher
// charging above this is displayed as the cap (1.16%) in official mode; the
// true rate is only revealed via the secret F2+O gesture. Mirrors the backend
// OFFICIAL_RATE_CAP and the receipt screen's masking.
export const OFFICIAL_RATE_CAP = 1.16;

// ─── Document-file helper ─────────────────────────────────────────────────────
// The documents table stores the literal string "No file" when a document row
// exists without an uploaded file. Treat that (and empty values) as no file at
// all so the UI never renders it as a broken image URL.
export const realDocFile = (file) =>
  file && typeof file === "string" && file !== "No file" ? file : null;

// ─── Field styling ───────────────────────────────────────────────────────────
// Hide the number-input spinner arrows so numeric fields look like plain text
// fields (they still only accept numbers). Kept as its own top-level selector
// keys so per-field "& .MuiInputBase-input" overrides can't clobber it.
export const noSpinnerSx = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
};

export const fieldSx = {
  ...noSpinnerSx,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: SURFACE,
    fontSize: "0.875rem",
    "& fieldset": {
      borderColor: "#9CA3AF", // Darker gray instead of BORDER (#E2E8F0)
      borderWidth: "2px", // Increased from 1.5px to 2px
    },
    "&:hover fieldset": {
      borderColor: BLUE_MED,
      borderWidth: "2.5px", // Increased from 2px to 2.5px
    },
    "&.Mui-focused fieldset": {
      borderColor: BLUE,
      borderWidth: "3px", // Increased from 2.5px to 3px
    },
  },
  "& .MuiInputLabel-root": { color: INK_MUTED, fontSize: "0.8rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: BLUE_DARK },
};

// Returns fieldSx merged with red-error overrides when the field has an error
export const getFieldSx = (hasError) =>
  hasError
    ? {
        ...fieldSx,
        "& .MuiOutlinedInput-root": {
          ...fieldSx["& .MuiOutlinedInput-root"],
          backgroundColor: "#FFF5F5",
          "& fieldset": {
            borderColor: ERROR_C,
            borderWidth: "2.5px", // Thicker error border
          },
          "&:hover fieldset": {
            borderColor: ERROR_C,
            borderWidth: "3px",
          },
          "&.Mui-focused fieldset": {
            borderColor: ERROR_C,
            borderWidth: "3px",
          },
        },
        "& .MuiInputLabel-root": { color: ERROR_C, fontSize: "0.8rem" },
        "& .MuiInputLabel-root.Mui-focused": { color: ERROR_C },
      }
    : fieldSx;

// ─── Shared select sx - UPDATED with thicker borders ────────────────────────────────────────
export const selectSx = {
  borderRadius: "8px",
  bgcolor: SURFACE,
  fontSize: "0.875rem",
  color: INK,
  ".MuiOutlinedInput-notchedOutline": {
    borderColor: "#9CA3AF", // Darker gray
    borderWidth: "2px", // Thicker
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: BLUE_MED,
    borderWidth: "2.5px",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: BLUE,
    borderWidth: "3px",
  },
};

// ─── TextField specific styling - UPDATED ───────────────────────────────────────────────────
export const textFieldSx = {
  ...noSpinnerSx,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: SURFACE,
    fontSize: "0.875rem",
    "& fieldset": {
      borderColor: "#9CA3AF",
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: BLUE_MED,
      borderWidth: "2.5px",
    },
    "&.Mui-focused fieldset": {
      borderColor: BLUE,
      borderWidth: "3px",
    },
  },
};

// ─── Autocomplete styling - UPDATED ─────────────────────────────────────────────────────────
export const autocompleteSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: SURFACE,
    fontSize: "0.875rem",
    "& fieldset": {
      borderColor: "#9CA3AF",
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: BLUE_MED,
      borderWidth: "2.5px",
    },
    "&.Mui-focused fieldset": {
      borderColor: BLUE,
      borderWidth: "3px",
    },
  },
};

// ─── Ticket-sequence nav button style ───────────────────────────────────────
export const navBtnSx = {
  border: `1.5px solid ${BORDER_MED}`,
  borderRadius: "8px",
  color: BLUE,
  p: "3px",
  bgcolor: SURFACE,
  "&:hover": { bgcolor: BLUE_PALE, borderColor: BLUE_MED },
  "&.Mui-disabled": { color: BORDER_MED, borderColor: BORDER },
};

// ─── MUI Theme ───────────────────────────────────────────────────────────────
export const appTheme = createTheme({
  palette: {
    primary: {
      main: BLUE,
      dark: BLUE_DARK,
      light: BLUE_LIGHT,
      contrastText: "#fff",
    },
    background: { default: BG, paper: SURFACE },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: '"DM Sans", "Segoe UI", "Roboto", sans-serif',
    h5: { fontWeight: 700, letterSpacing: "-0.02em" },
    h6: { fontWeight: 700 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 8,
          padding: "7px 18px",
          transition: "all 0.18s ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 4px 14px rgba(37,99,235,0.22)",
          },
        },
        containedPrimary: {
          background: BLUE,
          color: "#fff",
          "&:hover": { background: BLUE_DARK },
        },
        outlinedPrimary: {
          borderColor: BLUE_MED,
          color: BLUE,
          "&:hover": { borderColor: BLUE_DARK, bgcolor: BLUE_PALE },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: "0 1px 4px rgba(15,23,42,0.07)",
          border: `1px solid ${BORDER}`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: { fontWeight: 700, fontSize: "0.72rem", letterSpacing: "0.06em" },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, fontSize: "0.75rem" } },
    },
  },
});
